import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { programService } from '../../services/programService';
import { unitService } from '../../services/unitService';
import { systemService } from '../../services/systemService';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/layouts/AdminLayout';

const FinanceDashboard = ({ embedded = false, onBack = null, roleBadge = '' }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [billingMode, setBillingMode] = useState('program_based');
    const [programs, setPrograms] = useState([]);
    const [units, setUnits] = useState([]);
    const [expandedProgramId, setExpandedProgramId] = useState(null);

    // Form states mapped by ID
    const [programFees, setProgramFees] = useState({});
    const [unitFees, setUnitFees] = useState({});
    const [defaultUnitFees, setDefaultUnitFees] = useState({});
    
    // Filters and batch tracking
    const [programFilter, setProgramFilter] = useState('all');
    const [levelFilter, setLevelFilter] = useState('all');
    const [unitTypeFilter, setUnitTypeFilter] = useState('all');
    const [modifiedUnitIds, setModifiedUnitIds] = useState(new Set());

    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const formatLevelLabel = (levelStr) => {
        const mapping = {
            'level_3': 'Level 3 (NVC)',
            'level_4_artisan': 'Level 4 (Artisan)',
            'level_5_craft': 'Level 5 (Craft)',
            'level_6_diploma': 'Level 6 (Diploma)',
            '3': 'Level 3 (NVC)',
            '4': 'Level 4 (Artisan)',
            '5': 'Level 5 (Craft)',
            '6': 'Level 6 (Diploma)'
        };
        return mapping[levelStr] || levelStr;
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [progs, unts, instSettings] = await Promise.all([
                programService.getAll(),
                unitService.getAll(),
                systemService.getInstitutionDetails()
            ]);
            
            const bMode = instSettings?.billing_mode || 'program_based';
            setBillingMode(bMode);
            
            // Parse unit_type_fees from institution_settings
            let defaults = {};
            if (instSettings?.unit_type_fees) {
                try {
                    const parsed = typeof instSettings.unit_type_fees === 'string' 
                        ? JSON.parse(instSettings.unit_type_fees) 
                        : instSettings.unit_type_fees;
                    defaults = { ...defaults, ...parsed };
                } catch (e) {
                    console.warn('Failed to parse unit_type_fees', e);
                }
            }
            setDefaultUnitFees(defaults);

            setPrograms(progs || []);
            setUnits(unts || []);
            
            // Init Program Fees
            const pFees = {};
            (progs || []).forEach(p => {
                pFees[p.id] = {
                    tuitionFee: p.tuition_fee || p.tuitionFee || 0,
                    levelTuitionFees: p.levelTuitionFees || {}
                };
            });
            setProgramFees(pFees);

            // Init Unit Fees
            const uFees = {};
            (unts || []).forEach(u => {
                const uType = String(u.unitType || u.unit_type || 'core').toLowerCase();
                const inheritedTuitionFee = defaults[uType]?.tuition || 0;
                const inheritedExamFee = defaults[uType]?.exam || 0;
                
                const dbTuition = Number(u.tuition_fee || 0);
                const dbExam = Number(u.exam_fee || 0);
                
                uFees[u.id] = {
                    tuitionFee: dbTuition || inheritedTuitionFee,
                    examFee: dbExam || inheritedExamFee
                };
            });
            setUnitFees(uFees);
            
        } catch (error) {
            console.error('Error fetching finance data:', error);
            showToast('Failed to load finance data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleProgramFeeChange = (programId, field, value) => {
        setProgramFees(prev => ({
            ...prev,
            [programId]: {
                ...prev[programId],
                [field]: value
            }
        }));
    };

    const handleLevelFeeChange = (programId, level, value) => {
        setProgramFees(prev => ({
            ...prev,
            [programId]: {
                ...prev[programId],
                levelTuitionFees: {
                    ...prev[programId].levelTuitionFees,
                    [level]: value
                }
            }
        }));
    };

    const handleUnitFeeChange = (unitId, field, value) => {
        setUnitFees(prev => ({
            ...prev,
            [unitId]: {
                ...prev[unitId],
                [field]: value
            }
        }));
        setModifiedUnitIds(prev => new Set(prev).add(unitId));
    };

    const saveProgramFee = async (programId) => {
        setSaving(true);
        try {
            const fees = programFees[programId];
            await programService.update(programId, {
                tuitionFee: parseFloat(fees.tuitionFee) || 0,
                levelTuitionFees: fees.levelTuitionFees
            });
            showToast('Fees updated successfully!');
        } catch (error) {
            console.error('Failed to update fees:', error);
            showToast(`Failed to update fees: ${error?.message || error?.details || 'Unknown error'}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    const saveAllUnitFees = async () => {
        if (modifiedUnitIds.size === 0) {
            showToast('No changes to save.', 'error');
            return;
        }
        setSaving(true);
        try {
            const promises = Array.from(modifiedUnitIds).map(unitId => {
                const fees = unitFees[unitId];
                return unitService.update(unitId, {
                    tuition_fee: parseFloat(fees.tuitionFee) || 0,
                    exam_fee: parseFloat(fees.examFee) || 0
                });
            });
            await Promise.all(promises);
            setModifiedUnitIds(new Set());
            showToast('All modified unit fees updated successfully!');
        } catch (error) {
            console.error('Failed to update fees:', error);
            showToast('Failed to update some unit fees', 'error');
        } finally {
            setSaving(false);
        }
    };




    if (loading) {
        return (
            <div className="p-6 text-center text-muted-foreground">Loading finance data...</div>
        );
    }

    const renderContent = () => (
        <div className={embedded ? "space-y-5" : "space-y-6 max-w-7xl mx-auto py-6 px-4"}>
            {embedded && (
                <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm">
                    <div className="flex items-center gap-2.5 shrink-0">
                        {onBack && (
                            <>
                                <button onClick={onBack} className="text-xs font-semibold text-blue-700 hover:underline">← Staff dashboard</button>
                                <span className="text-slate-300 text-xs">/</span>
                            </>
                        )}
                        <h2 className="text-sm font-bold tracking-tight text-slate-900">Finance Management</h2>
                        {roleBadge && (
                            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 capitalize">
                                {roleBadge}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full border border-primary/20">
                        <Icon name="Settings" size={12} />
                        <span className="font-semibold capitalize">{billingMode.replace('_', ' ')} Mode</span>
                    </div>
                </div>
            )}

            {!embedded && (
                <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h2 className="text-xl font-bold text-foreground">Finance Management</h2>
                            <p className="text-sm text-muted-foreground">
                                Configure tuition and exam fees.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 text-sm bg-primary/10 text-primary px-3 py-1.5 rounded-full border border-primary/20">
                            <Icon name="Settings" size={14} />
                            <span className="font-semibold capitalize">{billingMode.replace('_', ' ')} Mode</span>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Active billing mode is determined by Institution Settings. 
                        {billingMode === 'program_based' ? ' Bills are generated based on Program definitions.' : ' Bills are generated based on registered Units.'}
                    </p>
                </div>
            )}

                {billingMode === 'program_based' && (
                    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
                        <div className="p-4 border-b border-border bg-muted/20">
                            <h3 className="font-semibold text-sm">Program Fee Configuration</h3>
                        </div>
                        <div className="divide-y divide-border">
                            {programs.map(program => (
                                <div key={program.id} className="p-4 hover:bg-muted/10 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 cursor-pointer" onClick={() => setExpandedProgramId(expandedProgramId === program.id ? null : program.id)}>
                                            <div className="flex items-center gap-2">
                                                <Icon 
                                                    name={expandedProgramId === program.id ? 'ChevronDown' : 'ChevronRight'} 
                                                    size={16} 
                                                    className="text-muted-foreground"
                                                />
                                                <h4 className="font-medium text-foreground">{program.name}</h4>
                                                <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">{program.code}</span>
                                                {program.levels?.length > 0 && (
                                                    <span className="text-[10px] uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full ml-2">
                                                        {expandedProgramId === program.id ? 'Hide Levels' : 'Set Per-Level Fees'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-muted-foreground">Base Tuition (KES):</span>
                                                <input 
                                                    type="number" 
                                                    className="w-24 text-sm px-2 py-1 rounded border border-border bg-background focus:ring-2 focus:ring-primary focus:outline-none"
                                                    value={programFees[program.id]?.tuitionFee || ''}
                                                    onChange={(e) => handleProgramFeeChange(program.id, 'tuitionFee', e.target.value)}
                                                />
                                            </div>
                                            <Button size="sm" onClick={() => saveProgramFee(program.id)} disabled={saving}>
                                                Save
                                            </Button>
                                        </div>
                                    </div>

                                    {expandedProgramId === program.id && program.levels?.length > 0 && (
                                        <div className="mt-4 pl-8 pt-4 border-t border-border/50">
                                            <h5 className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Per-Level Overrides</h5>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {program.levels.map(level => (
                                                    <div key={level} className="flex items-center justify-between bg-muted/20 p-2 rounded-md border border-border/50">
                                                        <span className="text-sm font-medium">{formatLevelLabel(level)}</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-muted-foreground text-xs">KES</span>
                                                            <input 
                                                                type="number" 
                                                                className="w-20 text-sm px-2 py-1 rounded border border-border bg-background focus:ring-2 focus:ring-primary focus:outline-none"
                                                                placeholder="Base"
                                                                value={programFees[program.id]?.levelTuitionFees?.[level] || ''}
                                                                onChange={(e) => handleLevelFeeChange(program.id, level, e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-2">
                                                If a level fee is left empty, it will default to the base tuition fee during billing.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {programs.length === 0 && (
                                <div className="p-8 text-center text-muted-foreground text-sm">
                                    No programs found.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {toast && (
                    <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full shadow-lg text-sm font-medium transition-all ${
                        toast.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    }`}>
                        {toast.message}
                    </div>
                )}



                {billingMode === 'unit_based' && (() => {
                    const filteredUnits = units.filter(u => {
                        if (programFilter !== 'all' && String(u.program_id) !== String(programFilter)) return false;
                        if (levelFilter !== 'all' && String(u.programLevel || u.program_level) !== String(levelFilter)) return false;
                        const uType = String(u.unitType || u.unit_type || 'core').toLowerCase();
                        if (unitTypeFilter !== 'all' && uType !== unitTypeFilter) return false;
                        return true;
                    });

                    return (
                        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-border bg-muted/20 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <h3 className="font-semibold text-sm">Unit Fee Configuration</h3>
                                    <p className="text-xs text-muted-foreground mt-0.5">Showing {filteredUnits.length} of {units.length} units.</p>
                                </div>
                                
                                <div className="flex flex-wrap items-center gap-3">
                                    <select 
                                        className="text-xs px-3 py-1.5 rounded-md border border-border bg-background focus:ring-2 focus:ring-primary focus:outline-none max-w-[200px] truncate"
                                        value={programFilter}
                                        onChange={e => setProgramFilter(e.target.value)}
                                    >
                                        <option value="all">All Programs</option>
                                        {Array.from(new Set(units.map(u => u.program_id).filter(Boolean)))
                                            .map(pid => programs.find(p => p.id === pid))
                                            .filter(Boolean)
                                            .map(p => (
                                                <option key={p.id} value={p.id}>{p.name || p.code || 'Unknown'}</option>
                                            ))
                                        }
                                    </select>

                                    <select 
                                        className="text-xs px-3 py-1.5 rounded-md border border-border bg-background focus:ring-2 focus:ring-primary focus:outline-none max-w-[150px] truncate"
                                        value={levelFilter}
                                        onChange={e => setLevelFilter(e.target.value)}
                                    >
                                        <option value="all">All Levels</option>
                                        {Array.from(new Set(
                                            units
                                                .filter(u => programFilter === 'all' || String(u.program_id) === String(programFilter))
                                                .map(u => u.programLevel || u.program_level)
                                                .filter(Boolean)
                                        )).map(l => (
                                            <option key={l} value={l}>{formatLevelLabel(l)}</option>
                                        ))}
                                    </select>

                                    <select 
                                        className="text-xs px-3 py-1.5 rounded-md border border-border bg-background focus:ring-2 focus:ring-primary focus:outline-none"
                                        value={unitTypeFilter}
                                        onChange={e => setUnitTypeFilter(e.target.value)}
                                    >
                                        <option value="all">All Types</option>
                                        {Array.from(new Set(
                                            units
                                                .filter(u => programFilter === 'all' || String(u.program_id) === String(programFilter))
                                                .filter(u => levelFilter === 'all' || String(u.programLevel || u.program_level) === String(levelFilter))
                                                .map(u => String(u.unitType || u.unit_type || 'core').toLowerCase())
                                        )).map(t => (
                                            <option key={t} value={t} className="capitalize">{t}</option>
                                        ))}
                                    </select>

                                    <Button size="sm" onClick={saveAllUnitFees} disabled={saving || modifiedUnitIds.size === 0}>
                                        Save Changes ({modifiedUnitIds.size})
                                    </Button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-muted/30 text-muted-foreground text-xs uppercase">
                                        <tr>
                                            <th className="px-4 py-3 font-medium">Code</th>
                                            <th className="px-4 py-3 font-medium">Unit Name</th>
                                            <th className="px-4 py-3 font-medium w-32">Tuition Fee (KES)</th>
                                            <th className="px-4 py-3 font-medium w-32">Exam Fee (KES)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {filteredUnits.map(unit => (
                                            <tr key={unit.id} className="hover:bg-muted/10">
                                                <td className="px-4 py-3 font-medium text-foreground">{unit.code}</td>
                                                <td className="px-4 py-3">{unit.name}</td>
                                                <td className="px-4 py-3">
                                                    <input 
                                                        type="number" 
                                                        className="w-full text-sm px-2 py-1 rounded border border-border bg-background focus:ring-2 focus:ring-primary focus:outline-none"
                                                        value={unitFees[unit.id]?.tuitionFee || ''}
                                                        onChange={(e) => handleUnitFeeChange(unit.id, 'tuitionFee', e.target.value)}
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <input 
                                                        type="number" 
                                                        className="w-full text-sm px-2 py-1 rounded border border-border bg-background focus:ring-2 focus:ring-primary focus:outline-none"
                                                        value={unitFees[unit.id]?.examFee || ''}
                                                        onChange={(e) => handleUnitFeeChange(unit.id, 'examFee', e.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredUnits.length === 0 && (
                                            <tr>
                                                <td colSpan="4" className="p-8 text-center text-muted-foreground text-sm">
                                                    No units match your filters.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })()}
            </div>
    );

    if (embedded) return renderContent();

    return (
        <AdminLayout title="Finance Management">
            {renderContent()}
        </AdminLayout>
    );
};

export default FinanceDashboard;
